import { z } from 'zod'
import { paths } from '@mc/paths'
import { CourseSubType } from 'types/data/Course'
import { type CourseTile } from './types'

const CourseTileResultSchema = z.object({
  id: z.coerce.string(),
  link: z.string(),
  slug: z.string(),
  type: z.string(),
  title: z.string(),
  duration: z.number(),
  nameplateImage: z.string(),
  backgroundImage: z.string().nullish(),
  subtitle: z.string().nullable(),
  primary2x3: z.string(),
  tag: z.string(),
  headline: z.string(),
  releaseDescription: z.string().nullable(),
  instructorName: z.string().nullish(),
  mediaUuid: z.string().nullable().optional(),
})

const CourseTileMapperSchema = z.object({
  slug: z.string(),
  type: z.string(),
  headline: z.string(),
  title: z.string(),
  nameplate: z.string().optional(),
  numChapters: z.number().nullable().optional(),
  durationInSeconds: z.number().nullable().optional(),
  durationAsCount: z.number().nullable().optional(),
  lessonCount: z.number().nullable().optional(),
  totalSeconds: z.number().nullable().optional(),
  backgroundImage: z.string().nullable().optional(),
  displaySubtitle: z.string().optional(),
  tag: z.string().nullable().optional(),
  releaseDescription: z.string().nullish(),
  contentCount: z.number().nullable().optional(),
  instructorName: z.string().nullable().optional(),
})

const warn = (..._args: unknown[]) => {
  // console.info(..._args)
}

export const prepareTile = (tile: unknown): CourseTile | null => {
  if (typeof tile !== 'object') {
    warn('invalid tile', tile)
    return null
  }
  const metaResult = CourseTileMapperSchema.safeParse(tile)
  if (!metaResult.success) {
    warn('meta err', { tile, issues: metaResult.error.issues })
    return null
  }

  const meta = metaResult.data

  const getDuration = () => {
    if (meta.type === CourseSubType.SERIES || meta.type === CourseSubType.CAMP)
      return (
        meta.numChapters ??
        meta.lessonCount ??
        meta.contentCount ??
        meta.durationAsCount
      )
    return meta.totalSeconds ?? meta.durationInSeconds
  }

  const result = CourseTileResultSchema.safeParse({
    ...tile,
    link: paths.courseTypePage(meta.type, meta.slug),
    nameplateImage: meta.nameplate,
    subtitle: meta.displaySubtitle || null,
    tag: meta.tag || '',
    headline: meta.type === CourseSubType.CAMP ? meta.title : meta.headline,
    releaseDescription: meta.releaseDescription || null,
    duration: getDuration(),
  })

  if (result.success) {
    return result.data
  } else {
    warn('tile err', { tile, meta, issues: result.error.issues })
    return null
  }
}

export const prepareTiles = (tiles: unknown[]): CourseTile[] => {
  if (!Array.isArray(tiles)) {
    warn('invalid tiles', tiles)
    return []
  }
  const parsedTiles = tiles.map((tile) => prepareTile(tile))
  return parsedTiles.filter(<T>(t: T | null): t is T => t !== null)
}
